import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, login, logout } = useAuth();

  const navLinks = [
    { name: 'Services', href: '/services' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <div className="glass border-t-0 border-x-0 border-b border-blue-500/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src="/logo.png" alt="NetEye Logo" className="w-10 h-10 rounded-xl object-cover shadow-[0_0_15px_rgba(59,130,246,0.3)]" />
              <span className="font-bold text-3xl tracking-widest uppercase bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent" style={{ fontFamily: "'Exo 2', sans-serif" }}>NETEYE</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(link.href)
                      ? 'text-blue-400'
                      : 'text-gray-300 hover:text-blue-400'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <>
                  <Link to="/dashboard">
                    <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-white/5" data-testid="dashboard-btn">
                      Dashboard
                    </Button>
                  </Link>
                  <Button 
                    onClick={logout}
                    variant="outline" 
                    className="border-blue-500/20 text-gray-300 hover:border-blue-400 hover:text-blue-400 hover:bg-blue-500/5 bg-transparent"
                    data-testid="logout-btn"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    onClick={login}
                    variant="ghost" 
                    className="text-gray-300 hover:text-white hover:bg-white/5"
                    data-testid="login-btn"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={login}
                    className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold hover:opacity-90 shadow-[0_4px_15px_rgba(59,130,246,0.2)] hover:shadow-[0_8px_25px_rgba(59,130,246,0.3)] transition-all border-none"
                    data-testid="get-started-btn"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 text-gray-300 hover:text-white"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-blue-500/10 bg-[#080B14]/95 backdrop-blur-lg"
            >
              <div className="px-6 py-4 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className={`block py-2 text-base font-medium transition-colors ${
                      isActive(link.href) ? 'text-blue-400' : 'text-gray-300 hover:text-blue-400'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-4 border-t border-blue-500/10 space-y-3">
                  {user ? (
                    <>
                      <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                        <Button className="w-full border-blue-500/20 hover:bg-blue-500/5 text-gray-300 bg-transparent" variant="outline">Dashboard</Button>
                      </Link>
                      <Button onClick={logout} className="w-full text-gray-300 hover:text-white" variant="ghost">Logout</Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={login} className="w-full text-gray-300 hover:text-white" variant="ghost">Sign In</Button>
                      <Button onClick={login} className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold border-none">
                        Get Started
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}
